import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { PickedItem } from 'src/data-capture/file-management/drop-zone/SharePointButton';
import { api } from 'src/common/utils/api';
import { UploadedFile } from './types';
import { uploadPool, UPLOAD_CONCURRENCY } from './uploadPool';
import { invalidateDataUploadFiles } from './invalidateDataUploadFiles';

export type OneDriveFileUploadInput = {
  items: PickedItem[];
  location?: string;
  modelId?: string;
  documentDate?: string | null;
  parentId?: string | null;
  isResolved?: boolean;
  isTemporary?: boolean;
};

type Props = {
  clientId?: string;
};

export const useOneDriveFileUpload = ({ clientId: overrideClientId = undefined }: Props = {}) => {
  const { clientId: currentClientId, client } = useCurrentClient();
  const clientId = overrideClientId ?? currentClientId;
  const familyId = client?.id === clientId ? (client.family?.id ?? client.familyId) : undefined;
  const queryClient = useQueryClient();
  const resource = `clients/${clientId}/data-upload-files/onedrive`;

  const mutation = useMutation({
    mutationFn: async (input: OneDriveFileUploadInput): Promise<UploadedFile[]> => {
      const items = input.items.filter(item => item.downloadUrl);

      const results = await uploadPool(
        items.map(item => ({
          task: async () => {
            const { data } = await api.post<UploadedFile[]>(resource, {
              files: [
                {
                  fileName: item.name || 'Unknown',
                  downloadUrl: item.downloadUrl!,
                  fileSize: item.size,
                  createdDateTime: item.createdDateTime,
                },
              ],
              modelId: input.modelId,
              documentDate: input.documentDate?.split('T')[0],
              parentId: input.parentId,
              isTemporary: input.isTemporary,
              isResolved: input.isResolved,
            });
            return data;
          },
          label: item.name || 'Unknown file',
        })),
        { concurrency: UPLOAD_CONCURRENCY, context: { clientId } },
      );

      return results.flat();
    },
    onSuccess: async () => {
      await invalidateDataUploadFiles(queryClient, { clientId, familyId });
      await queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'all-documents'] });
      if (familyId) {
        await queryClient.invalidateQueries({ queryKey: ['families', familyId, 'all-documents'] });
      }
    },
  });

  return {
    uploadOneDriveFiles: mutation.mutateAsync,
    isUploading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
};
